import type { ColorKey, Mode, Tokens } from './schema';

/** `surfaceMuted` -> `surface-muted`, `chart1` -> `chart-1`, `2xl` stays `2xl`. */
function kebab(key: string): string {
  return key
    .replace(/([a-z])(\d)/g, '$1-$2')
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .toLowerCase();
}

/** CSS variable of a colour key: `primarySoft` -> `--bl-primary-soft`. */
export function colorVarName(key: ColorKey): string {
  return `--bl-${kebab(key)}`;
}

/** All colours of one mode as `--bl-*` variables. */
export function colorVars(tokens: Tokens, mode: Mode): Record<string, string> {
  const vars: Record<string, string> = {};
  for (const [key, value] of Object.entries(tokens.color[mode])) {
    vars[colorVarName(key as ColorKey)] = value;
  }
  return vars;
}

/** Mode-independent variables: fonts (quoted), sizes and radii in px, unitless line heights. */
export function baseVars(tokens: Tokens): Record<string, string> {
  const vars: Record<string, string> = {};
  vars['--bl-font-arabic'] = `"${tokens.font.arabic}"`;
  vars['--bl-font-latin'] = `"${tokens.font.latin}"`;
  for (const [key, value] of Object.entries(tokens.size)) {
    vars[`--bl-size-${kebab(key)}`] = `${value}px`;
  }
  for (const [key, value] of Object.entries(tokens.lineHeight)) {
    vars[`--bl-line-height-${kebab(key)}`] = String(value);
  }
  for (const [key, value] of Object.entries(tokens.radius)) {
    vars[`--bl-radius-${kebab(key)}`] = `${value}px`;
  }
  tokens.space.forEach((value, index) => {
    vars[`--bl-space-${index}`] = `${value}px`;
  });
  return vars;
}

const block = (selector: string, scheme: Mode, vars: Record<string, string>): string => {
  const lines = Object.entries(vars).map(([name, value]) => `  ${name}: ${value};`);
  return `${selector} {\n  color-scheme: ${scheme};\n${lines.join('\n')}\n}\n`;
};

/** The dark-mode selector `@bloom/ui` has always used (a `data-theme` attribute on the root). */
export const DARK_SELECTOR = ':root[data-theme="dark"]';

/**
 * The tokens as CSS: all variables on `:root` (light colours plus fonts, sizes, radii, spacing),
 * then `:root[data-theme="dark"]` with only the colours that differ from light.
 *
 * Safe to put in a stylesheet: `parseTokens` restricts every value. Pass tokens that went through
 * `parseTokens` or `defineTokens`.
 */
export function tokensToCss(tokens: Tokens): string {
  const light = colorVars(tokens, 'light');
  const dark = colorVars(tokens, 'dark');
  const changed = Object.fromEntries(
    Object.entries(dark).filter(([name, value]) => light[name] !== value),
  );
  const root = block(':root', 'light', { ...light, ...baseVars(tokens) });
  if (Object.keys(changed).length === 0) return root;
  return `${root}\n${block(DARK_SELECTOR, 'dark', changed)}`;
}
